// Icon + word rendering for a model category, shared by every /photon page
// so categories always present the same way. The input side of the manifest
// modality string picks the label ("Image + text → text" and "Audio → text";
// the output side is dropped), and inputs map to the accepted category term
// in words: image + text reads "Vision-language" (VLM), audio reads
// "Speech recognition" (ASR). One icon per input modality stays in front.
// "Chat" is a pseudo-modality for the chat quickstart tabs: bubble icon,
// label passed through.

const ICON_PROPS = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const ModalityIcon = ({ kind, size }: { kind: string; size: number }) => {
  if (kind === "image")
    return (
      <svg {...ICON_PROPS} width={size} height={size}>
        <rect x="3" y="3" width="18" height="18" rx="0" />
        <circle cx="9" cy="9" r="2" />
        <path d="M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
      </svg>
    );
  if (kind === "text")
    return (
      <svg {...ICON_PROPS} width={size} height={size}>
        <path d="M17 6.1H3" />
        <path d="M21 12.1H3" />
        <path d="M15.1 18H3" />
      </svg>
    );
  if (kind === "chat")
    return (
      <svg {...ICON_PROPS} width={size} height={size}>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    );
  if (kind === "audio")
    return (
      <svg {...ICON_PROPS} width={size} height={size}>
        <path d="M2 10v3" />
        <path d="M6 6v11" />
        <path d="M10 3v18" />
        <path d="M14 8v7" />
        <path d="M18 5v13" />
        <path d="M22 10v3" />
      </svg>
    );
  return null;
};

const CATEGORY_LABELS: Record<string, string> = {
  "image+text": "Vision-language",
  audio: "Speech recognition",
  text: "Text",
  image: "Image",
  chat: "Chat",
};

/**
 * @param modality A manifest modality string ("Image + text → text") or an
 *   already input-only phrase ("Image + text", "Audio").
 */
export const ModalityInputs = ({
  modality,
  size = 15,
}: {
  modality: string;
  size?: number;
}) => {
  const inputs = modality
    .split("→")[0]
    .split("+")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
  const fallback = inputs.join(" + ");
  const label =
    CATEGORY_LABELS[inputs.join("+")] ??
    fallback.charAt(0).toUpperCase() + fallback.slice(1);
  return (
    <span className="photon-modality">
      {inputs.map((input) => (
        <ModalityIcon key={input} kind={input} size={size} />
      ))}
      <span>{label}</span>
    </span>
  );
};
